import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import { rtkApi } from '../../../RTK/api/rtkApi';
import {
  setSelectedCustomViewFolder,
  updateFavViewById,
  updateQueryGroups,
  updateQueryDisplayColumns,
  updateSelectedCustomFilterQueryName,
  updateFocusedParentFolder,
  setSelectedFilterQueryValidation,
  updateQueryGroupInfo,
  setActionsCount,
  setSpinnerView,
  updateShouldCallCheckAndAddUserToView,
  updateDefaultViewById,
} from '../actions/viewActions';
import {
  FolderType,
  IViewFolder,
  ViewCategory,
} from '../../interfaces/CustomViewFolder';
import {
  ICustomFilterQuery,
  IDeleteViewPayload,
  IselectedFilterQueryValidation,
} from '../../interfaces/Slice.types';
import { ManageViewConstants, ViewTypes } from '../../utils/ManageViewConstants';
import { getAllBranchValueShouldBeOpen } from '../../utils/helpers/generateTreeView';
import { getURLParameter } from 'src/utils/utils';
import {
  DefaultConfigurationTenantId,
} from '../../utils/ManageViewConfiguration';
import { getViewsDataApi } from 'src/features/HubOverview/store/service/multitenantDashboardApi';
import { FolderViewChild, FolderViewResponse, ViewFolderScopeType } from '../../interfaces/ViewFolderResponse';
import { mapArtemisFolderViewToFolderView } from '../../utils/helpers/MapViewFolder';
import { mapViewFolderChildToViewFolder } from '../../utils/helpers/MapViewFolderChildToViewFolder';
import { MapQueryGroupSchema } from '../../utils/helpers/MapQueryGroupSchema';
import { MapDisplayColumns } from '../../utils/helpers/MapDisplayColumns';
import { UserMetadata, ViewReference } from '../../interfaces/UserMetadataResponse';
import { mapCustomFilter } from '../../utils/helpers/MapCustomFilter';
import { setUserProfile } from '../actions/userAction';
import { IUserContext } from 'src/manageAction/interfaces/ManageAction';
import { IManageViewActions } from 'src/Common/dashboardSummary/store/typings';
import { ITenantData } from 'src/tenantConfig/interface/ITenantData';
import { mergeTenantConfig } from 'src/tenantConfig/slice/tenantSlice';
import { ITenant } from 'src/tenantConfig/interface';
import { IRAIDEntity } from 'src/Common/Models/RAIDEntity';

export interface ViewIdEntity {
  id: string;
}

export interface RAIDEntityResultsEntity {
  id: string;
  results: IRAIDEntity[];
}

export interface DefaultViewByTenant {
  tenantId: string;
  defaultViewId: string;
}

export interface INewViewDialogProperties {
  useCase: string;
  viewId: string;
  tenantId: string;
  viewToSave: Partial<ICustomFilterQuery>;
}

const viewFoldersAdapter = createEntityAdapter<IViewFolder>();
const customFiltersAdapter = createEntityAdapter<ICustomFilterQuery>();
const customFilterEntitiesPreviousStateAdapter = createEntityAdapter<ICustomFilterQuery>();
const favoritedViewIdsAdapter = createEntityAdapter<ViewIdEntity>();
const exportResultsAdapter = createEntityAdapter<RAIDEntityResultsEntity>();

const defaultedViewIdEntitiesByTenantAdapter = createEntityAdapter<DefaultViewByTenant>({
  selectId: (tenantData: DefaultViewByTenant) => tenantData.tenantId, // Set the key for the entity to be tenant id
});

const customFiltersResultsAdapater = createEntityAdapter<IManageViewActions>({
  selectId: (actionData: DefaultViewByTenant) => actionData.queryId, // Set the key for the entity to be tenant id
});

const tenantDataAdapter = createEntityAdapter<ITenantData>({
  selectId: (tenantData: ITenantData) => tenantData.tenantId, // Set the key for the entity to be tenant id
});

export interface CustomTreeViewState {
  viewFolders: IViewFolder[];
  viewFoldersEntities: EntityState<IViewFolder, string>;
  customFilters: ICustomFilterQuery[];
  customFiltersEntities: EntityState<ICustomFilterQuery, string>; //Represents the current state of the filter with unsaved changes
  customFiltersEntitiesPreviousState: EntityState<ICustomFilterQuery, string>; //Represents the previous state of the customFiltersEntities
  customFiltersResults: EntityState<IManageViewActions, string>;
  favoritedViewIdEntities: EntityState<ViewIdEntity, string>; // Favorite data comes from user metadata so we need to store a separate state. isFavorite should've ideally not been a part of customFilters or viewFolders
  defaultedViewIdEntitiesByTenant: EntityState<DefaultViewByTenant, string>; // Default data comes from user metadata so we need to store a separate state isDefault should've ideally not been a part of customFilters or viewFolders
  viewFoldersTenantDictionary: { [key: string]: IViewFolder[] };
  selectedCustomFilterQuery: ICustomFilterQuery | null;
  selectedCustomFilterQueryDictionary: { [key: string]: ICustomFilterQuery };
  selectedViewFolder: IViewFolder | null;
  selectedViewFolderDictionary: { [key: string]: IViewFolder };
  focusedParentFolder: IViewFolder | null;
  selectedFilterQueryValidation: IselectedFilterQueryValidation | null;
  treeViewOpenedParentFolder: string[];
  newlyCreatedOrClonedViewFlagId: string | null | undefined;
  actionsCount: number;
  isShowSpinner: boolean;
  initialSelectedCustomFilterQuery: ICustomFilterQuery | null;
  shouldCallCheckAndAddUserToView: boolean;
  isNewViewDialogOpen: boolean;
  newViewDialogProperties: INewViewDialogProperties | null;
  manageViewSelectedTenantId: string | null;
  exportResults: EntityState<RAIDEntityResultsEntity, string>;
  tenantData: EntityState<ITenantData, string>;
}

const initialState = {
  customFilters: [], // TODO: Deprecate this as we move to customFiltersEntities
  customFiltersEntities: customFiltersAdapter.getInitialState(),
  customFiltersEntitiesPreviousState: customFilterEntitiesPreviousStateAdapter.getInitialState(),
  customFiltersResults: customFiltersResultsAdapater.getInitialState(),
  viewFolders: [], // TODO: Deprecate this as we move to viewFoldersEntities
  viewFoldersEntities: viewFoldersAdapter.getInitialState(),
  favoritedViewIdEntities: favoritedViewIdsAdapter.getInitialState(),
  defaultedViewIdEntitiesByTenant: defaultedViewIdEntitiesByTenantAdapter.getInitialState(),
  viewFoldersTenantDictionary: {}, // TODO: Deprecate this
  selectedCustomFilterQuery: null,
  selectedCustomFilterQueryDictionary: {}, // TODO: Deprecate this
  selectedViewFolder: null,
  selectedViewFolderDictionary: {}, // TODO: Deprecate this
  focusedParentFolder: null,
  selectedFilterQueryValidation: null,
  treeViewOpenedParentFolder: [],
  newlyCreatedOrClonedViewFlagId: null,
  actionsCount: 0,
  isShowSpinner: false,
  initialSelectedCustomFilterQuery: null,
  shouldCallCheckAndAddUserToView: false,
  isNewViewDialogOpen: false,
  newViewDialogProperties: null,
  manageViewSelectedTenantId: null,
  exportResults: exportResultsAdapter.getInitialState(),
  tenantData: tenantDataAdapter.getInitialState(),
} as CustomTreeViewState;

const upsertFolderToStore = (
  state: CustomTreeViewState,
  viewFolderResponse: FolderViewResponse
) => {
  const newViewFolder: IViewFolder = mapArtemisFolderViewToFolderView(viewFolderResponse);

  viewFoldersAdapter.addOne(state.viewFoldersEntities, newViewFolder);

  const {
    viewFolders: newViewFolders, //View folders are the representation of the folder or view on the UI
    views: newViews, //Views represent the view metadata like query groups, display columns, etc
  }: { viewFolders: IViewFolder[]; views: ICustomFilterQuery[] } = processViewFolderChildren(
    viewFolderResponse,
    state
  );

  newViewFolders.push(newViewFolder);

  viewFoldersAdapter.setMany(state.viewFoldersEntities, newViewFolders);
  customFiltersAdapter.addMany(state.customFiltersEntities, newViews);
  customFilterEntitiesPreviousStateAdapter.addMany(
    state.customFiltersEntitiesPreviousState,
    newViews
  );
};

const upsertViewToStore = (state: CustomTreeViewState, viewFolderResponse: FolderViewResponse) => {
  const newViewFolder: IViewFolder = mapArtemisFolderViewToFolderView(viewFolderResponse);
  const mappedCustomFilter: ICustomFilterQuery = mapCustomFilter(viewFolderResponse);

  if (!mappedCustomFilter.id) return;

  const isFavorite = state.favoritedViewIdEntities.entities[mappedCustomFilter.id] ? true : false;

  const isDefault =
    state.defaultedViewIdEntitiesByTenant.entities[viewFolderResponse.TenantId]?.defaultViewId ===
    mappedCustomFilter.id;

  const newCustomFilter: ICustomFilterQuery = {
    ...mappedCustomFilter,
    isFavorite,
    isDefault,
  };
  newViewFolder.isDefault = isDefault;
  newViewFolder.isFavorite = isFavorite;

  viewFoldersAdapter.setOne(state.viewFoldersEntities, newViewFolder);
  customFiltersAdapter.setOne(state.customFiltersEntities, newCustomFilter);
  customFilterEntitiesPreviousStateAdapter.setOne(
    state.customFiltersEntitiesPreviousState,
    newCustomFilter
  );
};

const getSharedViewId = () => {
  return getURLParameter('viewID')?.toString() || '';
};

// Helper functions to bind data to store start
const setViewsToStore = (state, action) => {
  const views: ICustomFilterQuery[] = action?.payload?.customFilters ?? [];

  const favoriteViewIds: string[] =
    views.filter((view) => view.isFavorite).map((view) => view.id) ?? [];
  favoritedViewIdsAdapter.addMany(
    state.favoritedViewIdEntities,
    favoriteViewIds?.map((id) => ({ id }))
  );

  customFiltersAdapter.setMany(state.customFiltersEntities, action.payload.customFilters);
  customFilterEntitiesPreviousStateAdapter.setMany(state.customFiltersEntitiesPreviousState, views);

  state.customFilters = [
    ...state.customFilters,
    ...action.payload.customFilters.filter(
      (newFilter: any) =>
        !state.customFilters.some((existingFilter: any) => existingFilter.id === newFilter.id)
    ),
  ];

  const tenantId = state.manageViewSelectedTenantId;
  const selectedView = tenantId
    ? sessionStorage.getItem(`selectedView_${tenantId}`) || sessionStorage.getItem('selectedView')
    : sessionStorage.getItem('selectedView');
  const sharedViewId = getSharedViewId();

  //On initial load / If selected query obj is null
  if (state.selectedCustomFilterQuery === null) {
    let selectedCustomFilterQuery = null;
    let customFilterQuery = null;
    //On initail load viewID exist in url parameter
    if (sharedViewId !== '') {
      if (action.payload.customFilters.length > 0) {
        customFilterQuery = action.payload.customFilters.find((view) => view.id === sharedViewId);
        if (customFilterQuery) {
          selectedCustomFilterQuery = customFilterQuery;
          state.selectedCustomFilterQuery = selectedCustomFilterQuery;
          state.initialSelectedCustomFilterQuery = selectedCustomFilterQuery;
        } else {
          //viewID in URL is temporary view id
          customFilterQuery = action.payload.customFilters.find(
            (view) => view.temporaryViewID === sharedViewId
          );
          if (customFilterQuery) {
            //The temporary view id is founded in the customFilters
            selectedCustomFilterQuery = customFilterQuery;
            state.selectedCustomFilterQuery = selectedCustomFilterQuery;
            state.initialSelectedCustomFilterQuery = selectedCustomFilterQuery;
          } else {
            //Temporary view ID is not found in the customFilters
            state.shouldCallCheckAndAddUserToView = true;
          }
        }
      }
    } else {
      if (selectedView && selectedView !== 'null') {
        customFilterQuery = action.payload.customFilters.find(
          (view) => view.temporaryViewID === selectedView || view.id === selectedView
        );
      } else {
        customFilterQuery = action.payload.customFilters.find((view) => !!view.isDefault);
      }
      if (customFilterQuery) {
        selectedCustomFilterQuery = customFilterQuery;
      } else {
        //System view's assign to me and set the object to selected folder if default view is not found
        selectedCustomFilterQuery =
          action.payload.customFilters.find(
            (view) =>
              view.type.toLocaleLowerCase() === ViewTypes.system.toLocaleLowerCase() &&
              view.name === ManageViewConstants.Views.AssignedToMe
          ) ?? null;
      }
      state.selectedCustomFilterQuery = selectedCustomFilterQuery;
      state.initialSelectedCustomFilterQuery = selectedCustomFilterQuery;
    }
  } else {
    let selectedCustomFilterQuery = action.payload.customFilters.find(
      (view) => view.id === state.selectedCustomFilterQuery?.id
    );
    state.initialSelectedCustomFilterQuery = selectedCustomFilterQuery ?? null;
  }
};

const setViewsFolderToStore = (state, action) => {
  const viewFolders: IViewFolder[] = action.payload ?? [];
  viewFoldersAdapter.setMany(state.viewFoldersEntities, action.payload);

  const favoriteViewFolderIds: string[] = viewFolders
    ?.filter((folder) => folder.isFavorite)
    ?.map((folder) => folder.id);

  favoritedViewIdsAdapter.addMany(
    state.favoritedViewIdEntities,
    favoriteViewFolderIds?.map((id) => ({ id }))
  );

  state.viewFolders = action.payload ?? [];

  const sharedViewId = getSharedViewId();

  //On initial load / If selected obj is null, set the selected folder to default view
  if (state.selectedViewFolder === null) {
    let selectedViewFolder = null;
    // Try to get tenant-specific selected view first, fallback to global if not available
    const tenantId = state.manageViewSelectedTenantId;
    const selectedView = tenantId
      ? sessionStorage.getItem(`selectedView_${tenantId}`) || sessionStorage.getItem('selectedView')
      : sessionStorage.getItem('selectedView');
    //On initail load viewID exist in url parameter
    if (sharedViewId !== '') {
      //find view based on url view id and set the object to selected folder
      const folderFoundById = action.payload.find((view: any) => view.id === sharedViewId);
      if (folderFoundById) {
        selectedViewFolder = folderFoundById;
      } else {
        const folderFoundByTempId = action.payload.find(
          (view: any) => view.temporaryViewID === sharedViewId
        );
        if (folderFoundByTempId) {
          selectedViewFolder = folderFoundByTempId;
        }
        //Note : No need else section as shouldCallCheckAndAddUserToView is handled in setViewsToStore fn
      }
    } else {
      //find default view and set the object to selected folder
      let viewWithFolder = null;
      if (selectedView && selectedView !== 'null') {
        viewWithFolder = action.payload.find(
          (view) => view.temporaryViewID === selectedView || view.id === selectedView
        );
      } else {
        viewWithFolder = action.payload.find((view) => !!view.isDefault);
      }
      if (viewWithFolder) {
        selectedViewFolder = viewWithFolder;
      } else {
        //System view's assign to me and set the object to selected folder if default view is not found
        selectedViewFolder =
          action.payload.find(
            ({ category, name }) =>
              category === ViewCategory.SystemView &&
              name === ManageViewConstants.Views.AssignedToMe
          ) ?? null;
      }
    }
    state.selectedViewFolder = selectedViewFolder;
  }

  //After saveCustomQuery matchFulfilled run the below code to set the selected folder
  //If newlyCreatedOrClonedViewFlagId is not null then set the selected folder to the newly created view
  if (state.newlyCreatedOrClonedViewFlagId) {
    state.selectedViewFolder =
      action.payload.find(({ id }) => id === state.newlyCreatedOrClonedViewFlagId) ?? null;

    const branchShouldBeOpen = getAllBranchValueShouldBeOpen(
      action.payload,
      state.newlyCreatedOrClonedViewFlagId
    );

    state.treeViewOpenedParentFolder = branchShouldBeOpen;

    const newFocusedFolderId = branchShouldBeOpen[branchShouldBeOpen.length - 1];
    const focusedFolder = action.payload.find((folder) => folder.id === newFocusedFolderId) ?? null;
    state.focusedParentFolder = focusedFolder;

    //After setting the selected folder to the newly created view, set the newlyCreatedOrClonedViewFlagId to null
    state.newlyCreatedOrClonedViewFlagId = null;
  }
};
// Helper functions to bind data to store end

const viewSlice = createSlice({
  name: 'view',
  initialState,
  reducers: {
    getViewsFromOverview: (state, action) => {
      //Get data from overview page
      setViewsToStore(state, action);
    },
    getViewsFolderFromOverview: (state, action) => {
      //Get data from overview page
      setViewsFolderToStore(state, action);
    },
    setViews: (state, action) => {
      state.customFilters = action.payload;
    },
    setViewFolders: (state, action) => {
      state.viewFolders = action.payload;
    },
    setSelectedCustomFilterQuery(state, action) {
      if (action.payload) {
        state.selectedCustomFilterQuery = action.payload;
      }
    },
    setSelectedViewFolder(state, action) {
      if (action.payload) {
        state.selectedViewFolder = action.payload;
      }
    },
    setSelectedViewDefault(state, action: PayloadAction<{ tenantId?: string }>) {
      const { tenantId } = action.payload;
      if (tenantId) {
        const selectedViewFolder = state.viewFolders.find(
          ({ isDefault, tenantId: folderTenantId }) => isDefault && folderTenantId === tenantId
        );
        if (selectedViewFolder) {
          state.selectedViewFolder = selectedViewFolder;
        }
      }
    },
    addView(state, action: PayloadAction<ICustomFilterQuery>) {
      const existingIndex: number = state.customFilters.findIndex(
        (view) => view.id === action.payload.id
      );
      if (existingIndex !== -1) {
        // Replace the existing view
        state.customFilters[existingIndex] = action.payload;
      } else {
        // Add the new view
        state.customFilters.push(action.payload);
      }
    },
    addViewFolder(state, action: PayloadAction<IViewFolder>) {
      if (action.payload) {
        state.viewFolders.push(action.payload);
      }
    },
    setViewFoldersTenantDictionaryKey(state, action) {
      state.viewFoldersTenantDictionary[action.payload.key] = action.payload.value;
    },
    addViewFolderEntities: (state, action) => {
      viewFoldersAdapter.addMany(state.viewFoldersEntities, action.payload);
    },
    addViewFolderEntity: (state, action) => {
      viewFoldersAdapter.addOne(state.viewFoldersEntities, action.payload);
    },
    setViewFolderEntities: (state, action) => {
      viewFoldersAdapter.setMany(state.viewFoldersEntities, action.payload);
    },
    addViewEntity: (state, action) => {
      customFiltersAdapter.addOne(state.customFiltersEntities, action.payload);
      customFilterEntitiesPreviousStateAdapter.addOne(
        state.customFiltersEntitiesPreviousState,
        action.payload
      );
    },
    addViewEntities: (state, action) => {
      customFiltersAdapter.addMany(state.customFiltersEntities, action.payload);
      customFilterEntitiesPreviousStateAdapter.addMany(
        state.customFiltersEntitiesPreviousState,
        action.payload
      );
    },
    setViewEntities: (state, action) => {
      customFiltersAdapter.setMany(state.customFiltersEntities, action.payload);
    },
    updateViewById: (state, action) => {
      customFiltersAdapter.updateOne(state.customFiltersEntities, {
        id: action.payload.id,
        changes: action.payload.changes,
      });
    },
    favoriteViewById: (state, action) => {
      customFiltersAdapter.updateOne(state.customFiltersEntities, {
        id: action.payload.id,
        changes: { isFavorite: action.payload.isFavorite },
      });
    },
    removeFolderById: (state, action) => {
      viewFoldersAdapter.removeOne(state.viewFoldersEntities, action.payload);
    },
    setIsNewViewDialogOpen: (state, action) => {
      state.isNewViewDialogOpen = action.payload;
    },
    dashboardViewSaveAs: (state, action) => {
      tenantDataAdapter.updateOne(state.tenantData, {
        id: action.payload.tenantId,
        changes: { selectedViewId: action.payload.viewId },
      });
      state.newViewDialogProperties = action.payload;
    },
    setManageViewSelectedTenantId: (state, action) => {
      state.manageViewSelectedTenantId = action.payload;
    },
    setSelectedViewForTenant: (
      state,
      action: PayloadAction<{ tenantId: string; viewId: string }>
    ) => {
      const tenantId: string = action.payload.tenantId;
      const viewId: string = action.payload.viewId;
      if (tenantId && viewId) {
        tenantDataAdapter.updateOne(state.tenantData, {
          id: tenantId,
          changes: { selectedViewId: viewId },
        });
      }
    },
    setDefaultViewForTenant: (
      state,
      action: PayloadAction<{ tenantId: string; viewId: string }>
    ) => {
      const tenant = state.tenantData.entities[action.payload.tenantId];

      if (tenant) {
        tenantDataAdapter.updateOne(state.tenantData, {
          id: tenant.tenantId,
          changes: { defaultViewId: action.payload.viewId },
        });
      }
    },
    //Revert the changes made in the edit view
    cancelViewEditById: (state, action) => {
      const viewId: string = action.payload;
      if (viewId) {
        const previousView: ICustomFilterQuery =
          state.customFiltersEntitiesPreviousState.entities[viewId];

        customFiltersAdapter.setOne(state.customFiltersEntities, previousView);
      }
    },
    setExportResultsByViewId: (state, action) => {
      const viewId: string = action.payload.viewId;
      const results: IRAIDEntity[] = action.payload.data;

      if (viewId) {
        exportResultsAdapter.setOne(state.exportResults, { id: viewId, results });
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setSpinnerView, (state, action) => {
      state.isShowSpinner = action.payload;
    });
    builder.addCase(setUserProfile, (state, action) => {
      const userProfile: IUserContext = action.payload;

      if (userProfile) {
        const defaultViewId: string | undefined = userProfile?.userSettings?.defaultView?.ViewId;
        if (defaultViewId) {
          state.defaultedViewIdEntitiesByTenant = defaultedViewIdEntitiesByTenantAdapter.addOne(
            state.defaultedViewIdEntitiesByTenant,
            { tenantId: DefaultConfigurationTenantId, defaultViewId: defaultViewId }
          );

          tenantDataAdapter.upsertOne(state.tenantData, {
            tenantId: DefaultConfigurationTenantId,
            selectedViewId: defaultViewId,
            defaultViewId: defaultViewId,
          });
        }
      }
    });
    builder.addCase(setSelectedCustomViewFolder, (state, action) => {
      const view: ICustomFilterQuery = state.customFiltersEntities.entities[action.payload];

      if (view) {
        state.selectedCustomFilterQuery = view;
        state.initialSelectedCustomFilterQuery = view;
      } else {
        state.selectedCustomFilterQuery = null;
        state.initialSelectedCustomFilterQuery = null;
      }

      if (state.viewFoldersEntities.entities[action.payload]) {
        state.selectedViewFolder = state.viewFoldersEntities.entities[action.payload];
      } else {
        state.selectedViewFolder = null;
      }
    });
    builder.addCase(updateSelectedCustomFilterQueryName, (state, action) => {
      if (state.selectedCustomFilterQuery) {
        state.selectedCustomFilterQuery.name = action.payload;
      }
    });
    builder.addCase(updateQueryGroupInfo, (state, action) => {
      if (state.selectedCustomFilterQuery) {
        state.selectedCustomFilterQuery.groupsInfo = action.payload;
        updateViewById({
          id: state.selectedCustomFilterQuery.id,
          changes: { groupsInfo: action.payload },
        });
      }
    });
    builder.addCase(updateFavViewById, (state, action) => {
      if (state.selectedCustomFilterQuery) {
        state.selectedCustomFilterQuery.isFavorite = action.payload.isFavorite;
      }
      state?.customFilters?.map((view) => {
        if (view.id === action.payload.id) {
          view.isFavorite = action.payload.isFavorite;
        }
        return view;
      });
    });
    builder.addCase(updateDefaultViewById, (state, action) => {
      const defaultedViewId: string | undefined = action?.payload;

      if (defaultedViewId) {
        const currentDefaultedViewId: string =
          state.defaultedViewIdEntitiesByTenant.entities[DefaultConfigurationTenantId]
            ?.defaultViewId;
        if (currentDefaultedViewId) {
          const oldDefaultedViewFolder: IViewFolder = {
            ...state.viewFoldersEntities.entities[currentDefaultedViewId],
            isDefault: false,
          };
          const oldDefaultedCustomFilter: ICustomFilterQuery = {
            ...state.customFiltersEntities.entities[currentDefaultedViewId],
            isDefault: false,
          };

          viewFoldersAdapter.setOne(state.viewFoldersEntities, oldDefaultedViewFolder);
          customFiltersAdapter.setOne(state.customFiltersEntities, oldDefaultedCustomFilter);
        }
        const newDefaultedViewFolder: IViewFolder = {
          ...state.viewFoldersEntities.entities[defaultedViewId],
          isDefault: true,
        };
        const newDefaultedCustomFilter: ICustomFilterQuery = {
          ...state.customFiltersEntities.entities[defaultedViewId],
          isDefault: true,
        };

        viewFoldersAdapter.setOne(state.viewFoldersEntities, newDefaultedViewFolder);
        customFiltersAdapter.setOne(state.customFiltersEntities, newDefaultedCustomFilter);
        defaultedViewIdEntitiesByTenantAdapter.setOne(state.defaultedViewIdEntitiesByTenant, {
          tenantId: DefaultConfigurationTenantId,
          defaultViewId: defaultedViewId,
        });
      }
    });
    builder.addCase(updateQueryGroups, (state, action) => {
      if (state.selectedCustomFilterQuery) {
        state.selectedCustomFilterQuery.queryGroups = action.payload;
        updateViewById({
          id: state.selectedCustomFilterQuery.id,
          changes: { queryGroups: action.payload },
        });
      }
    });
    builder.addCase(updateQueryDisplayColumns, (state, action) => {
      if (state.selectedCustomFilterQuery) {
        state.selectedCustomFilterQuery.displayColumn = action.payload ?? [];
      }
    });
    builder.addCase(updateFocusedParentFolder, (state, action) => {
      state.focusedParentFolder = action.payload ?? null;
    });
    builder.addCase(setSelectedFilterQueryValidation, (state, action) => {
      state.selectedFilterQueryValidation = action.payload ?? null;
    });
    builder.addCase(setActionsCount, (state, action) => {
      state.actionsCount = action.payload;
    });
    builder.addCase(updateShouldCallCheckAndAddUserToView, (state, action) => {
      state.shouldCallCheckAndAddUserToView = action.payload;
    });
    builder.addCase(mergeTenantConfig, (state, action: PayloadAction<ITenant[]>) => {
      const tenants: ITenant[] = action.payload?.value ?? action?.payload ?? [];
      tenants?.forEach((tenant) => {
        tenantDataAdapter.addOne(state.tenantData, {
          tenantId: tenant.TenantID,
          defaultViewId: null,
          selectedViewId: null,
        });
      });
    });
    builder.addMatcher(rtkApi.endpoints.updateCustomViewFolder.matchFulfilled, (state, action) => {
      state.selectedCustomFilterQuery = action.payload;
    });
    builder.addMatcher(rtkApi.endpoints.GetViewsWithFolders.matchFulfilled, (state, action) => {
      setViewsFolderToStore(state, action);
    });
    builder.addMatcher(rtkApi.endpoints.GetViews.matchFulfilled, (state, action) => {
      setViewsToStore(state, action);
    });
    builder.addMatcher(rtkApi.endpoints.addSharedView.matchFulfilled, (state) => {
      //This will help to refresh the view and query and trigger On initial load logic
      state.selectedViewFolder = null;
      state.selectedCustomFilterQuery = null;
    });
    builder.addMatcher(rtkApi.endpoints.deleteUserView.matchFulfilled, (state) => {
      //Find default view and set the object to selected folder and query . if default view is not found then set the system view's assign to me
      const defaultViewSelectedFolder = state.viewFolders.find(
        ({ isDefault }) => isDefault === true
      );
      if (defaultViewSelectedFolder) {
        state.selectedViewFolder = defaultViewSelectedFolder;
        const selectedCustomFilterQuery = state.customFilters.find(
          ({ id }) => id === defaultViewSelectedFolder.id
        );
        if (selectedCustomFilterQuery) {
          state.selectedCustomFilterQuery = selectedCustomFilterQuery;
        }
      } else {
        //System view's assign to me and set the object to selected folder and query
        const selectedViewFolder = state.viewFolders.find(
          ({ category, name }) =>
            category === ViewCategory.SystemView && name === ManageViewConstants.Views.AssignedToMe
        );
        if (selectedViewFolder) {
          state.selectedViewFolder = selectedViewFolder;
          const selectedCustomFilterQuery = state.customFilters.find(
            ({ id }) => id === selectedViewFolder.id
          );
          if (selectedCustomFilterQuery) {
            state.selectedCustomFilterQuery = selectedCustomFilterQuery;
          }
        }
      }
    });
    builder.addMatcher(
      rtkApi.endpoints.deleteUserView.matchRejected,
      (state, { payload, meta }) => {
        if (payload && 'originalStatus' in payload && payload?.originalStatus === 200) {
          const viewDeletePayload: IDeleteViewPayload = meta?.arg?.originalArgs;
          const viewId = viewDeletePayload?.id;

          if (viewId) {
            customFiltersAdapter.removeOne(state.customFiltersEntities, viewId);
            customFilterEntitiesPreviousStateAdapter.removeOne(
              state.customFiltersEntitiesPreviousState,
              viewId
            );
            viewFoldersAdapter.removeOne(state.viewFoldersEntities, viewId);
          }
        }
      }
    );
    builder.addMatcher(rtkApi.endpoints.deleteFolder.matchFulfilled, (state, action) => {
      const folderId: string | undefined = action?.meta?.arg?.originalArgs;
      if (folderId) {
        viewFoldersAdapter.removeOne(state.viewFoldersEntities, folderId);
      }
    });
    builder.addMatcher(
      rtkApi.endpoints.deleteUserView.matchRejected,
      (state, { payload, meta }) => {
        // Response is not JSON so we need to check the status code
        if (payload && 'originalStatus' in payload && payload?.originalStatus === 200) {
          const folderId: string | undefined = meta?.arg?.originalArgs?.id;
          if (folderId) {
            viewFoldersAdapter.removeOne(state.viewFoldersEntities, folderId);
          }
        }
      }
    );
    builder.addMatcher(rtkApi.endpoints.saveCustomQuery.matchFulfilled, (state, action) => {
      state.selectedCustomFilterQuery = action.payload;
      customFiltersAdapter.addOne(state.customFiltersEntities, action.payload);
      customFilterEntitiesPreviousStateAdapter.addOne(
        state.customFiltersEntitiesPreviousState,
        action.payload
      );
      state.newlyCreatedOrClonedViewFlagId = action.payload.id;

      const viewFoldersArray = Object.values(state.viewFoldersEntities.entities);

      const branchShouldBeOpen = getAllBranchValueShouldBeOpen(viewFoldersArray, action.payload.id);

      state.treeViewOpenedParentFolder = branchShouldBeOpen;
    });
    builder.addMatcher(rtkApi.endpoints.createFolder.matchFulfilled, (state, action) => {
      viewFoldersAdapter.addOne(state.viewFoldersEntities, action.payload);
    });
    builder.addMatcher(
      rtkApi.endpoints.updateAddViewToFavoritesData.matchFulfilled,
      (state, action) => {
        const viewId: string | undefined = action.meta.arg.originalArgs;

        if (viewId) {
          viewFoldersAdapter.updateOne(state.viewFoldersEntities, {
            id: viewId,
            changes: { isFavorite: true },
          });
          customFiltersAdapter.updateOne(state.customFiltersEntities, {
            id: viewId,
            changes: { isFavorite: true },
          });
          favoritedViewIdsAdapter.addOne(state.favoritedViewIdEntities, { id: viewId });
        }
      }
    );
    builder.addMatcher(
      rtkApi.endpoints.updateRemoveViewFromFavoritesData.matchFulfilled,
      (state, action) => {
        const viewId: string | undefined = action.meta.arg.originalArgs;

        if (viewId) {
          viewFoldersAdapter.updateOne(state.viewFoldersEntities, {
            id: viewId,
            changes: { isFavorite: false },
          });
          customFiltersAdapter.updateOne(state.customFiltersEntities, {
            id: viewId,
            changes: { isFavorite: false },
          });
          favoritedViewIdsAdapter.removeOne(state.favoritedViewIdEntities, viewId);
        }
      }
    );
    builder.addMatcher(rtkApi.endpoints.updateCustomViewFolder.matchFulfilled, (state, action) => {
      customFiltersAdapter.updateOne(state.customFiltersEntities, {
        id: action.payload.id,
        changes: action.payload,
      });
    });
    builder.addMatcher(
      getViewsDataApi.endpoints.createFolderView.matchFulfilled,
      (state, action) => {
        const newViewFolderResponse: FolderViewResponse = action.payload;

        const newViewFolder: IViewFolder = mapArtemisFolderViewToFolderView(newViewFolderResponse);

        //TODO-RM Deprecate logic for view since we split the view and folder APIs
        if (newViewFolderResponse.DocType === 'View') {
          const newCustomFilter: ICustomFilterQuery = mapCustomFilter(newViewFolderResponse);
          viewFoldersAdapter.addOne(state.viewFoldersEntities, newViewFolder);
          customFilterEntitiesPreviousStateAdapter.addOne(
            state.customFiltersEntitiesPreviousState,
            newCustomFilter
          );
          customFiltersAdapter.addOne(state.customFiltersEntitiesPreviousState, newCustomFilter);
        } else {
          viewFoldersAdapter.addOne(state.viewFoldersEntities, newViewFolder);

          const {
            viewFolders: newViewFolders,
            views: newViews,
          }: { viewFolders: IViewFolder[]; views: ICustomFilterQuery[] } =
            processViewFolderChildren(newViewFolderResponse, state);

          newViewFolders.push(newViewFolder);

          viewFoldersAdapter.setMany(state.viewFoldersEntities, newViewFolders);
          customFiltersAdapter.addMany(state.customFiltersEntities, newViews);
          customFilterEntitiesPreviousStateAdapter.addMany(
            state.customFiltersEntitiesPreviousState,
            newViews
          );
        }
      }
    );
    builder.addMatcher(getViewsDataApi.endpoints.createView.matchFulfilled, (state, action) => {
      const newViewFolderResponse: FolderViewResponse = action.payload;
      const newViewFolder: IViewFolder = mapArtemisFolderViewToFolderView(newViewFolderResponse);
      const newCustomFilter: ICustomFilterQuery = mapCustomFilter(newViewFolderResponse);

      viewFoldersAdapter.addOne(state.viewFoldersEntities, newViewFolder);
      customFiltersAdapter.addOne(state.customFiltersEntities, newCustomFilter);
      customFilterEntitiesPreviousStateAdapter.addOne(
        state.customFiltersEntitiesPreviousState,
        newCustomFilter
      );

      if (newViewFolderResponse.TenantId) {
        tenantDataAdapter.updateOne(state.tenantData, {
          id: newViewFolderResponse.TenantId,
          changes: { selectedViewId: newViewFolderResponse.id },
        });
      }
    });
    builder.addMatcher(getViewsDataApi.endpoints.updateView.matchFulfilled, (state, action) => {
      const updatedViewResponse: FolderViewResponse = action.payload;

      const updatedCustomFilter: ICustomFilterQuery = mapCustomFilter(updatedViewResponse);

      if (updatedCustomFilter && updatedViewResponse?.id) {
        const isFavorite: boolean =
          state.favoritedViewIdEntities.entities?.[updatedCustomFilter?.id];
        const isDefault: boolean =
          state.defaultedViewIdEntitiesByTenant.entities?.[updatedCustomFilter?.tenantId]
            ?.defaultViewId === updatedCustomFilter?.id;

        updatedCustomFilter.isDefault = isDefault;
        updatedCustomFilter.isFavorite = isFavorite;
        updatedCustomFilter.default = isDefault;

        customFilterEntitiesPreviousStateAdapter.updateOne(
          state.customFiltersEntitiesPreviousState,
          {
            id: updatedViewResponse?.id,
            changes: updatedCustomFilter,
          }
        );

        customFiltersAdapter.updateOne(state.customFiltersEntities, {
          id: updatedViewResponse?.id,
          changes: updatedCustomFilter,
        });

        // Update view folders entities
        if (updatedViewResponse?.Name) {
          viewFoldersAdapter.updateOne(state.viewFoldersEntities, {
            id: updatedViewResponse?.id,
            changes: { name: updatedViewResponse?.Name ?? '' },
          });
        }
      }
    });
    builder.addMatcher(getViewsDataApi.endpoints.deleteView.matchFulfilled, (state, action) => {
      const deletedViewArgs = action.meta.arg.originalArgs as { id: string; tenantId: string };
      const deletedViewId: string = deletedViewArgs.id;
      const tenantId: string = deletedViewArgs.tenantId;

      if (deletedViewId) {
        viewFoldersAdapter.removeOne(state.viewFoldersEntities, deletedViewId);
        customFiltersAdapter.removeOne(state.customFiltersEntities, deletedViewId);
        customFilterEntitiesPreviousStateAdapter.removeOne(
          state.customFiltersEntitiesPreviousState,
          deletedViewId
        );
        favoritedViewIdsAdapter.removeOne(state.favoritedViewIdEntities, deletedViewId);

        const defaultViewByTenantId: string | null =
          state.tenantData.entities[tenantId]?.defaultViewId;

        if (defaultViewByTenantId !== deletedViewId) {
          tenantDataAdapter.updateOne(state.tenantData, {
            id: tenantId,
            changes: { selectedViewId: defaultViewByTenantId },
          });
        } else {
          //If the deleted view is the default view then set the selected view to All Actions
          const allActionsFilter: ICustomFilterQuery = Object.values(
            state.customFiltersEntities.entities
          )?.find((filter) => filter.name === 'All Actions');

          tenantDataAdapter.updateOne(state.tenantData, {
            id: tenantId,
            changes: { selectedViewId: allActionsFilter.id, defaultViewId: allActionsFilter.id }, //TODO-RM Need behaviour on this
          });

          defaultedViewIdEntitiesByTenantAdapter.setOne(state.defaultedViewIdEntitiesByTenant, {
            tenantId: tenantId,
            defaultViewId: allActionsFilter.id,
          });

          customFilterEntitiesPreviousStateAdapter.updateOne(
            state.customFiltersEntitiesPreviousState,
            {
              id: allActionsFilter.id,
              changes: { isDefault: true },
            }
          );

          customFiltersAdapter.updateOne(state.customFiltersEntities, {
            id: allActionsFilter.id,
            changes: { isDefault: true },
          });

          viewFoldersAdapter.updateOne(state.viewFoldersEntities, {
            id: allActionsFilter.id,
            changes: { isDefault: true },
          });
        }
      }
    });
    builder.addMatcher(getViewsDataApi.endpoints.deleteFolder.matchFulfilled, (state, action) => {
      const deletedViewArgs = action.meta.arg.originalArgs as { id: string; tenantId: string };
      const deletedViewId: string = deletedViewArgs.id;

      viewFoldersAdapter.removeOne(state.viewFoldersEntities, deletedViewId);
    });
    builder.addMatcher(
      getViewsDataApi.endpoints.getViews.matchFulfilled,
      (state, action: PayloadAction<FolderViewResponse[]>) => {
        const getViewsResponse: FolderViewResponse[] = action.payload;

        getViewsResponse &&
          getViewsResponse?.forEach((viewFolderResponse: FolderViewResponse) => {
            upsertViewToStore(state, viewFolderResponse);
          });
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.getViewById.matchFulfilled,
      (state, action: PayloadAction<FolderViewResponse>) => {
        const viewFolderResponse: FolderViewResponse = action.payload;

        if (viewFolderResponse?.SourceViewId || viewFolderResponse?.ScopeType === 'SharedWithMe') {
          const viewFolderResponseSharedWithMe = {
            ...viewFolderResponse,
            ScopeType: 'SharedWithMe',
            ParentId: viewFolderResponse.TenantId,
          };
          upsertViewToStore(state, viewFolderResponseSharedWithMe);
        } else {
          viewFolderResponse && upsertViewToStore(state, viewFolderResponse);
        }
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.getFolderById.matchFulfilled,
      (state, action: PayloadAction<FolderViewResponse>) => {
        const viewFolderResponse: FolderViewResponse = action.payload;

        upsertFolderToStore(state, viewFolderResponse);
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.getFolders.matchFulfilled,
      (state, action: PayloadAction<FolderViewResponse[]>) => {
        const viewFolderResponses: FolderViewResponse[] = action.payload;

        viewFolderResponses.forEach((viewFolderResponse: FolderViewResponse) => {
          upsertFolderToStore(state, viewFolderResponse);
        });
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.shareViewWithMe.matchFulfilled,
      () => {
        // No-op: shared view handling is performed elsewhere
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.createUserMetadata.matchFulfilled,
      (state, action) => {
        const userMetaData: UserMetadata = action.payload;

        const addedFavoriteIds: string[] = userMetaData?.FavoriteViews?.map(
          (viewReference) => viewReference.Id
        );

        if (addedFavoriteIds) {
          viewFoldersAdapter.updateMany(
            state.viewFoldersEntities,
            addedFavoriteIds?.map((id: string) => ({
              id,
              changes: { isFavorite: true },
            }))
          );

          customFiltersAdapter.updateMany(
            state.customFiltersEntities,
            addedFavoriteIds?.map((id: string) => ({
              id,
              changes: { isFavorite: true },
            }))
          );

          favoritedViewIdsAdapter.addMany(
            state.favoritedViewIdEntities,
            addedFavoriteIds?.map((id: string) => ({ id }))
          );
        }

        //Add new default view
        if (userMetaData.DefaultView) {
          viewFoldersAdapter.updateOne(state.viewFoldersEntities, {
            id: userMetaData.DefaultView.Id,
            changes: { isDefault: true },
          });
          customFiltersAdapter.updateOne(state.customFiltersEntities, {
            id: userMetaData.DefaultView.Id,
            changes: { isDefault: true },
          });
          defaultedViewIdEntitiesByTenantAdapter.setOne(state.defaultedViewIdEntitiesByTenant, {
            tenantId: userMetaData.TenantId,
            defaultViewId: userMetaData.DefaultView.Id,
          });
        }

        if (userMetaData.TenantId) {
          const tenantData: ITenantData = state.tenantData.entities[userMetaData.TenantId];

          if (!tenantData.defaultViewId) {
            tenantDataAdapter.updateOne(state.tenantData, {
              id: userMetaData.TenantId,
              changes: { defaultViewId: userMetaData.DefaultView.Id },
            });
          }

          if (!tenantData.selectedViewId) {
            tenantDataAdapter.updateOne(state.tenantData, {
              id: userMetaData.TenantId,
              changes: { selectedViewId: userMetaData.DefaultView.Id },
            });
          }
        }
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.getUserMetadata.matchFulfilled,
      (state, action) => {
        const userMetaDataList: UserMetadata[] = action.payload;

        userMetaDataList?.forEach((userMetaData: UserMetadata) => {
          //Create a folder in memory to store the users shared views
          const sharedViewsFolderForTenant = {
            id: userMetaData.TenantId,
            name: 'Shared With Me',
            tenantId: userMetaData.TenantId,
            parentId: null,
            category: ViewCategory.Folder,
            folderType: FolderType.SharedWithMe,
            children: [],
            isFavorite: false,
            isDefault: false,
            createdOn: '',
            level: 0,
            temporaryViewID: null,
          };

          viewFoldersAdapter.addOne(state.viewFoldersEntities, sharedViewsFolderForTenant);

          userMetaData?.SharedWithMeViews?.forEach((viewReference: ViewReference) => {
            const newViewFolder: IViewFolder = {
              id: viewReference.Id,
              name: viewReference.Name,
              isFavorite: false,
              isDefault: false,
              tenantId: userMetaData.TenantId,
              parentId: userMetaData.TenantId, //TODO-RM: Update to use an actual shared views folder id instead of just using the tenant id
              createdOn: null, //TODO-RM: Need to check if this is correct
              category: ViewCategory.SharedWithMe,
              folderType: FolderType.SharedWithMe,
              temporaryViewID: null,
              children: null,
              level: 0,
            };

            viewFoldersAdapter.addOne(state.viewFoldersEntities, newViewFolder);
          });

          userMetaData?.FavoriteViews?.forEach((viewReference: ViewReference) => {
            favoritedViewIdsAdapter.addOne(state.favoritedViewIdEntities, { id: viewReference.Id });

            if (state.viewFoldersEntities.entities[viewReference.Id]) {
              viewFoldersAdapter.updateOne(state.viewFoldersEntities, {
                id: viewReference.Id,
                changes: { isFavorite: true },
              });
            } else {
              const newViewFolder: IViewFolder = {
                id: viewReference.Id,
                name: viewReference.Name,
                isFavorite: true,
                isDefault: false,
                tenantId: userMetaData.TenantId,
                parentId: null,
                createdOn: null,
                category: ViewCategory.UserView,
                folderType: FolderType.User,
                temporaryViewID: null,
                children: null,
                level: 0,
              };

              viewFoldersAdapter.addOne(state.viewFoldersEntities, newViewFolder);
            }

            if (state.customFiltersEntities.entities[viewReference.Id]) {
              customFiltersAdapter.updateOne(state.customFiltersEntities, {
                id: viewReference.Id,
                changes: { isFavorite: true },
              });
            }
          });
          if (userMetaData.DefaultView) {
            const userMetaDataViewReference: ViewReference = userMetaData.DefaultView;

            defaultedViewIdEntitiesByTenantAdapter.setOne(state.defaultedViewIdEntitiesByTenant, {
              tenantId: userMetaData.TenantId,
              defaultViewId: userMetaDataViewReference.Id,
            });

            if (state.viewFoldersEntities.entities[userMetaDataViewReference.Id]) {
              viewFoldersAdapter.updateOne(state.viewFoldersEntities, {
                id: userMetaDataViewReference.Id,
                changes: { isDefault: true },
              });
            } else {
              //If there isn't data in the store, that means we haven't loaded from API
              // Update with a skeleton view folder in the meantime.
              //We should embed more data in cosmos to avoid this
              const newViewFolder: IViewFolder = {
                id: userMetaDataViewReference.Id,
                name: userMetaDataViewReference.Name,
                isFavorite: false,
                isDefault: true,
                tenantId: userMetaData.TenantId,
                parentId: null,
                createdOn: null,
                category: ViewCategory.UserView,
                folderType: FolderType.User,
                temporaryViewID: null,
                children: null,
                level: 0,
              };

              viewFoldersAdapter.addOne(state.viewFoldersEntities, newViewFolder);
            }

            if (state.customFiltersEntities.entities[userMetaDataViewReference.Id]) {
              customFiltersAdapter.updateOne(state.customFiltersEntities, {
                id: userMetaDataViewReference.Id,
                changes: { isDefault: true },
              });
            }

            const tenant: ITenantData | null = state.tenantData.entities[userMetaData.TenantId];

            if (tenant) {
              tenantDataAdapter.updateOne(state.tenantData, {
                id: tenant.tenantId,
                changes: { defaultViewId: userMetaData.DefaultView.Id },
              });
            }
          }
        });
      }
    );
    builder.addMatcher(
      getViewsDataApi.endpoints.updateUserMetadata.matchFulfilled,
      (state, action) => {
        const userMetaData: UserMetadata = action.payload;

        const removedFavoriteIds: string[] = state.favoritedViewIdEntities.ids.filter(
          (id) => !userMetaData.FavoriteViews.some((viewReference) => viewReference.Id === id)
        );
        const addedFavoriteIds: string[] = userMetaData?.FavoriteViews?.map(
          (viewReference) => viewReference.Id
        );

        const removedDefaultedViewIdList: string[] = Object.values(
          state.defaultedViewIdEntitiesByTenant.entities
        ).map((entity) => entity.defaultViewId);

        const removedDefaultIds: string[] = removedDefaultedViewIdList?.filter(
          (id) => !userMetaData.DefaultView || userMetaData.DefaultView.Id !== id
        );

        if (removedFavoriteIds.length > 0) {
          viewFoldersAdapter.updateMany(
            state.viewFoldersEntities,
            removedFavoriteIds?.map((id: string) => ({
              id,
              changes: { isFavorite: false },
            }))
          );

          customFiltersAdapter.updateMany(
            state.customFiltersEntities,
            removedFavoriteIds?.map((id: string) => ({
              id,
              changes: { isFavorite: false },
            }))
          );

          favoritedViewIdsAdapter.removeMany(state.favoritedViewIdEntities, removedFavoriteIds);
        }

        if (addedFavoriteIds) {
          viewFoldersAdapter.updateMany(
            state.viewFoldersEntities,
            addedFavoriteIds?.map((id: string) => ({
              id,
              changes: { isFavorite: true },
            }))
          );

          customFiltersAdapter.updateMany(
            state.customFiltersEntities,
            addedFavoriteIds?.map((id: string) => ({
              id,
              changes: { isFavorite: true },
            }))
          );

          favoritedViewIdsAdapter.addMany(
            state.favoritedViewIdEntities,
            addedFavoriteIds?.map((id: string) => ({ id }))
          );
        }

        //Remove old default view
        if (removedDefaultIds.length > 0) {
          viewFoldersAdapter.updateMany(
            state.viewFoldersEntities,
            removedDefaultIds?.map((id: string) => ({
              id,
              changes: { isDefault: false },
            }))
          );

          customFiltersAdapter.updateMany(
            state.customFiltersEntities,
            removedDefaultIds?.map((id: string) => ({
              id,
              changes: { isDefault: false },
            }))
          );
        }

        //Add new default view
        if (userMetaData.DefaultView) {
          viewFoldersAdapter.updateOne(state.viewFoldersEntities, {
            id: userMetaData.DefaultView.Id,
            changes: { isDefault: true },
          });
          customFiltersAdapter.updateOne(state.customFiltersEntities, {
            id: userMetaData.DefaultView.Id,
            changes: { isDefault: true },
          });
          defaultedViewIdEntitiesByTenantAdapter.setOne(state.defaultedViewIdEntitiesByTenant, {
            tenantId: userMetaData.TenantId,
            defaultViewId: userMetaData.DefaultView.Id,
          });

          const tenant = state.tenantData.entities[userMetaData.TenantId];

          if (tenant) {
            tenantDataAdapter.updateOne(state.tenantData, {
              id: tenant.tenantId,
              changes: { defaultViewId: userMetaData.DefaultView.Id },
            });
          }
        }
      }
    );
  },
});

// Helper function to process view folder children
function processViewFolderChildren(viewFolderResponse: FolderViewResponse, state: any) {
  const viewFolders: IViewFolder[] = [];
  const views: ICustomFilterQuery[] = [];

  if (!viewFolderResponse?.Children || viewFolderResponse.Children.length === 0)
    return { views, viewFolders };

  // Determine if parent is a "My Views" folder — only User-scoped views and sub-folders belong there
  const isMyViewsFolder: boolean =
    viewFolderResponse.DocType === 'Folder' &&
    viewFolderResponse.Name === 'My Views' &&
    viewFolderResponse.ScopeType === ViewFolderScopeType.System;

  viewFolderResponse.Children?.forEach((child: FolderViewChild) => {
    // Skip non-User-scoped views inside "My Views" folders
    if (
      isMyViewsFolder &&
      child.DocType === 'View' &&
      child.ScopeType !== ViewFolderScopeType.User
    ) {
      return;
    }

    const isFavorite = state.favoritedViewIdEntities.entities[child.Id] ? true : false;
    const isDefault =
      state.defaultedViewIdEntitiesByTenant.entities[viewFolderResponse.TenantId]?.defaultViewId ===
      child.Id;

    const childViewFolder: IViewFolder = mapViewFolderChildToViewFolder(child);
    childViewFolder.isFavorite = isFavorite;
    childViewFolder.isDefault = isDefault;

    if (viewFolderResponse.TenantId) {
      childViewFolder.tenantId = viewFolderResponse.TenantId;
    }

    viewFolders.push(childViewFolder);

    if (child.DocType === 'View') {
      views.push({
        id: child.Id,
        name: child.Name,
        tenantId: viewFolderResponse?.TenantId,
        queryGroups: child.QueryGroups ? MapQueryGroupSchema(child.QueryGroups) : [],
        groupsInfo: child.GroupsInfo ?? [],
        displayColumn: MapDisplayColumns(child.DisplayColumns ?? []),
        parentId: viewFolderResponse.id,
        createdOn: null,
        createdBy: null,
        isFavorite,
        isDefault,
        viewGroupID: null,
        default: false,
        type: 'User',
        adoFolderId: null,
        temporaryViewID: null,
        analytics: null,
        gridFormat: null,
        children: null,
        adoQueryId: null,
        wiql: null,
        expiredOn: null,
      });
    }
  });

  return { views, viewFolders };
}

export default viewSlice.reducer;

export const {
  getViewsFromOverview,
  getViewsFolderFromOverview,
  setViewFolders,
  setSelectedViewFolder,
  setSelectedViewForTenant,
  setDefaultViewForTenant,
  setSelectedCustomFilterQuery,
  addView,
  addViewFolder,
  setViewFoldersTenantDictionaryKey,
  addViewFolderEntities,
  addViewFolderEntity,
  addViewEntity,
  addViewEntities,
  updateViewById,
  setViewFolderEntities,
  setViewEntities,
  removeFolderById,
  setIsNewViewDialogOpen,
  setManageViewSelectedTenantId,
  cancelViewEditById,
  dashboardViewSaveAs,
  setExportResultsByViewId,
} = viewSlice.actions;
// Generated by GitHub Copilot | Updated on: 2026-03-30 | 10 lines modified | Filter non-User-scoped views from My Views folder children
